/**
 * RAG Query Builder - Faceted Knowledge Base Search (CCE-008, CCE-011)
 * 
 * Provides UI for filtering knowledge base queries by classification facets:
 * - Journey Stage (awareness, consideration, decision, retention)
 * - Audience Level (executive, manager, practitioner, technical, end_user)
 * - Industry (technology, financial, healthcare, etc.)
 * - Technical Depth (executive, business, technical, deep_dive)
 * - Content Type (whitepaper, case_study, blog, etc.)
 * 
 * Mobile Features (CCE-011):
 * - Collapsible filter panel on mobile (<768px)
 * - Toggle button to show/hide filters
 * 
 * Created: February 4, 2026
 * Epic: CCE - Content Classification Enhancement
 */

import React, { useState } from 'react';
import { Search, Filter, X } from 'lucide-react';

export interface FacetFilters {
  journey_stage?: string;
  audience_level?: string;
  industry?: string;
  technical_depth?: string;
  content_type?: string;
  min_confidence?: number;
}

interface RAGQueryBuilderProps {
  onSearch: (query: string, facets: FacetFilters) => void;
  isLoading?: boolean;
}

export const RAGQueryBuilder: React.FC<RAGQueryBuilderProps> = ({ 
  onSearch, 
  isLoading = false 
}) => {
  const [query, setQuery] = useState("");
  const [facets, setFacets] = useState<FacetFilters>({});
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  const handleFacetChange = (facet: keyof FacetFilters, value: string) => {
    setFacets(prev => ({
      ...prev,
      [facet]: value || undefined  // Remove empty strings
    }));
  };

  const handleSearch = () => {
    if (query.trim()) {
      onSearch(query, facets);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      handleSearch();
    }
  };

  return (
    <div className="space-y-4 p-4 bg-white rounded-lg shadow">
      {/* Mobile Filter Toggle Button */}
      <div className="md:hidden flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Search Filters</h3>
        <button
          onClick={() => setIsMobileFilterOpen(!isMobileFilterOpen)}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label={isMobileFilterOpen ? "Hide filters" : "Show filters"}
        >
          {isMobileFilterOpen ? (
            <X className="h-5 w-5" />
          ) : (
            <Filter className="h-5 w-5" />
          )}
        </button>
      </div>

      {/* Search Input */}
      <div className="relative">
        <input
          type="text"
          placeholder="Search knowledge base..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyPress={handleKeyPress}
          className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          disabled={isLoading}
        />
        <Search className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
      </div>

      {/* Facet Filters - Collapsible on mobile */}
      <div className={`${isMobileFilterOpen ? 'block' : 'hidden'} md:block`}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        
        {/* Journey Stage */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Journey Stage
          </label>
          <select
            value={facets.journey_stage || ""}
            onChange={(e) => handleFacetChange("journey_stage", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={isLoading}
          >
            <option value="">Any Stage</option>
            <option value="AWARENESS">Awareness</option>
            <option value="CONSIDERATION">Consideration</option>
            <option value="DECISION">Decision</option>
            <option value="RETENTION">Retention</option>
            <option value="EXPANSION">Expansion</option>
          </select>
        </div>

        {/* Audience Level */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Audience Level
          </label>
          <select
            value={facets.audience_level || ""}
            onChange={(e) => handleFacetChange("audience_level", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={isLoading}
          >
            <option value="">Any Level</option>
            <option value="executive">Executive</option>
            <option value="manager">Manager</option>
            <option value="practitioner">Practitioner</option>
            <option value="technical">Technical</option>
            <option value="end_user">End User</option>
          </select>
        </div>

        {/* Industry */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Industry
          </label>
          <select
            value={facets.industry || ""}
            onChange={(e) => handleFacetChange("industry", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={isLoading}
          >
            <option value="">Any Industry</option>
            <option value="technology">Technology</option>
            <option value="financial">Financial Services</option>
            <option value="healthcare">Healthcare</option>
            <option value="manufacturing">Manufacturing</option>
            <option value="retail">Retail</option>
            <option value="government">Government</option>
            <option value="education">Education</option>
            <option value="media">Media</option>
            <option value="telecom">Telecommunications</option>
            <option value="energy">Energy</option>
            <option value="professional_services">Professional Services</option>
            <option value="cross_industry">Cross-Industry</option>
          </select>
        </div>

        {/* Technical Depth */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Technical Depth
          </label>
          <select
            value={facets.technical_depth || ""}
            onChange={(e) => handleFacetChange("technical_depth", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={isLoading}
          >
            <option value="">Any Depth</option>
            <option value="executive">Executive Summary</option>
            <option value="business">Business-Focused</option>
            <option value="technical">Technical</option>
            <option value="deep_dive">Deep Dive</option>
          </select>
        </div>

        {/* Content Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Content Type
          </label>
          <select
            value={facets.content_type || ""}
            onChange={(e) => handleFacetChange("content_type", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={isLoading}
          >
            <option value="">Any Type</option>
            <option value="whitepaper">Whitepaper</option>
            <option value="case_study">Case Study</option>
            <option value="datasheet">Datasheet</option>
            <option value="blog">Blog Post</option>
            <option value="ebook">eBook</option>
            <option value="video">Video</option>
            <option value="webinar">Webinar</option>
            <option value="presentation">Presentation</option>
            <option value="one_pager">One-Pager</option>
          </select>
        </div>

        {/* Confidence Threshold */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Min Confidence: {(facets.min_confidence || 0.5) * 100}%
          </label>
          <input
            type="range"
            min="0"
            max="100"
            step="5"
            value={(facets.min_confidence || 0.5) * 100}
            onChange={(e) => handleFacetChange("min_confidence", (parseInt(e.target.value) / 100).toString())}
            className="w-full"
            disabled={isLoading}
          />
          <div className="flex justify-between text-xs text-gray-500">
            <span>0%</span>
            <span>50%</span>
            <span>100%</span>
          </div>
        </div>
      </div>
      </div>

      {/* Active Filters Summary */}
      {Object.keys(facets).filter(k => facets[k as keyof FacetFilters]).length > 0 && (
        <div className="flex flex-wrap gap-2 p-2 bg-blue-50 rounded">
          <span className="text-sm font-medium text-blue-700">Active Filters:</span>
          {Object.entries(facets).map(([key, value]) => 
            value && key !== 'min_confidence' && (
              <span key={key} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                {key.replace('_', ' ')}: {value}
              </span>
            )
          )}
        </div>
      )}

      {/* Search Button */}
      <button
        onClick={handleSearch}
        disabled={!query.trim() || isLoading}
        className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
          !query.trim() || isLoading
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : 'bg-blue-600 text-white hover:bg-blue-700'
        }`}
      >
        {isLoading ? 'Searching...' : 'Search Knowledge Base'}
      </button>

      {/* Clear Filters */}
      {(query || Object.keys(facets).length > 0) && (
        <button
          onClick={() => {
            setQuery("");
            setFacets({});
          }}
          className="w-full py-2 px-4 text-gray-600 hover:text-gray-800 text-sm"
          disabled={isLoading}
        >
          Clear All Filters
        </button>
      )}
    </div>
  );
};

export default RAGQueryBuilder;
